import { CommonModule } from '@angular/common';
import { Component, DestroyRef, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { MultiSelectModule } from 'primeng/multiselect';
import { debounceTime, distinctUntilChanged, Subject, switchMap } from 'rxjs';
import { PaginationComponent } from '../../shared/components/pagination/pagination.component';
import {
  getNextOrderDocumentStatus,
  ORDER_DOCUMENT_STATUS_CLASSES,
  ORDER_DOCUMENT_STATUS_LABELS,
  ORDER_DOCUMENT_STATUS_OPTIONS,
  WERK_LABELS,
} from '../../shared/constants/order-document-labels';
import { canAccess, ORDER_DOCUMENT_ROLES } from '../../shared/constants/role-access';
import { EStorageKeys } from '../../shared/enums/storage-keys.enum';
import { DeleteConfirmationComponent } from '../../shared/modals/delete-confirmation/delete-confirmation.component';
import { ActivityType, IOrderDocument, OrderDocumentStatusEnum, ProcessingType, WerkType } from '../../shared/models/order-document.model';
import { IUser, UserRole } from '../../shared/models/user.model';
import { AuthService } from '../../shared/services/auth.service';
import { FilterStorageService } from '../../shared/services/filter-storage.service';
import { HelperService } from '../../shared/services/helper.service';
import { ModalService } from '../../shared/services/modal.service';
import { OrderDocumentService } from '../../shared/services/order-document.service';
import { ToastService } from '../../shared/services/toast.service';

@Component({
  selector: 'app-order-documents',
  standalone: true,
  imports: [CommonModule, FormsModule, PaginationComponent, MultiSelectModule],
  templateUrl: './order-documents.component.html',
  styleUrls: ['./order-documents.component.scss'],
})
export class OrderDocumentsComponent implements OnInit {
  public currentUser: IUser | null = null;

  public orderDocuments: IOrderDocument[] = [];
  public isLoading = false;

  public searchTerm = '';
  public searchTerms = new Subject<string>();

  public currentPage = 1;
  public pageSize = 10;
  public totalItems = 0;
  public totalPages = 1;
  public sortField = 'createdAt';
  public sortDirection = 'desc';

  public statusFilter: OrderDocumentStatusEnum[] = [];
  public OrderDocumentStatusEnum = OrderDocumentStatusEnum;
  public statusOptions = ORDER_DOCUMENT_STATUS_OPTIONS;

  // Every trigger (initial load, search, sort, page change, post-delete refresh) funnels through
  // this Subject and is switchMap'd into the actual HTTP call, so a new request always cancels
  // whatever previous one is still in flight. Without that, fast typing in the search box (or any
  // slow/backed-up request) piles up concurrent, uncancelled GETs to the same endpoint instead of
  // replacing them — which is what caused the runaway/duplicate requests here.
  private readonly reload = new Subject<void>();

  constructor(
    private router: Router,
    private authService: AuthService,
    private orderDocumentService: OrderDocumentService,
    public helperService: HelperService,
    private toastService: ToastService,
    private modalService: ModalService,
    private destroyRef: DestroyRef,
    private filterStorageService: FilterStorageService
  ) { }

  ngOnInit(): void {
    this.authService.currentUser$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(user => {
      this.currentUser = user;
    });

    this.restoreStatusFilter();

    this.reload
      .pipe(
        switchMap(() => {
          this.isLoading = true;
          const statusFilterValue = this.statusFilter?.length ? this.statusFilter.join(',') : '';
          return this.orderDocumentService.getAll(
            this.currentPage,
            this.pageSize,
            this.searchTerm,
            this.sortField,
            this.sortDirection,
            statusFilterValue
          );
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: response => {
          if (response.DATA) {
            this.orderDocuments = response.DATA.data;
            this.totalItems = response.DATA.total;
            this.totalPages = response.DATA.totalPages;
          }
          this.isLoading = false;
        },
        error: () => {
          console.error('Error loading order documents');
          this.isLoading = false;
        },
      });

    this.loadOrderDocuments();

    this.searchTerms
      .pipe(debounceTime(300), distinctUntilChanged(), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.currentPage = 1;
        this.loadOrderDocuments();
      });
  }

  public loadOrderDocuments(): void {
    this.reload.next();
  }

  public onSearchChange(): void {
    this.searchTerms.next(this.searchTerm);
  }

  public clearSearch(): void {
    this.searchTerm = '';
    this.searchTerms.next('');
  }

  public onPageChange(page: number): void {
    this.currentPage = page;
    this.loadOrderDocuments();
  }

  public onSort(field: string): void {
    if (this.sortField === field) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortField = field;
      this.sortDirection = 'asc';
    }
    this.currentPage = 1;
    this.loadOrderDocuments();
  }

  public getSortIcon(field: string): string {
    return this.helperService.getSortIcon(this.sortField, this.sortDirection, field);
  }

  public onStatusFilterChange(): void {
    this.currentPage = 1;
    this.persistStatusFilter();
    this.loadOrderDocuments();
  }

  private restoreStatusFilter(): void {
    this.statusFilter = this.filterStorageService.restore(
      EStorageKeys.ORDER_DOCUMENT_STATUS_FILTER,
      this.statusOptions.map(o => o.value)
    );
  }

  private persistStatusFilter(): void {
    this.filterStorageService.persist(EStorageKeys.ORDER_DOCUMENT_STATUS_FILTER, this.statusFilter || []);
  }

  public getCustomerLabel(doc: IOrderDocument): string {
    if (doc.order) {
      return `${doc.order.internalOrderId} - ${doc.order.companyName}`;
    }
    return doc.tempCustomer || '—';
  }

  /** True when the Kunde column is showing the free-text tempCustomer instead of a real linked order. */
  public isTempCustomer(doc: IOrderDocument): boolean {
    return !doc.order && !!doc.tempCustomer;
  }

  /** Werk column: documents saved before Werk existed fall back to Werk 1, like the form does on load. */
  public getWerkLabel(doc: IOrderDocument): string {
    return WERK_LABELS[doc.factory ?? WerkType.WERK_1];
  }

  public getProcessingLabel(processing: ProcessingType): string {
    switch (processing) {
      case ProcessingType.RADIAL:
        return 'Radial';
      case ProcessingType.SMD:
        return 'SMD';
      default:
        return processing;
    }
  }

  public getActivityLabel(activity: ActivityType): string {
    switch (activity) {
      case ActivityType.SCHNEIDEN:
        return 'Schneiden';
      case ActivityType.BIEGEN:
        return 'Biegen';
      case ActivityType.TROCKNEN:
        return 'Trocknen';
      case ActivityType.VEREINZELN:
        return 'Vereinzeln';
      case ActivityType.SPLEISSEN:
        return 'Spleißen';
      case ActivityType.DREHEN:
        return 'Drehen';
      case ActivityType.ENTGURTEN_NEUGURTEN:
        return 'Entgurten + Neugurten';
      case ActivityType.KONFEKTIONIEREN:
        return 'Konfektionieren';
      default:
        return activity;
    }
  }

  public getStatusName(status: OrderDocumentStatusEnum): string {
    return ORDER_DOCUMENT_STATUS_LABELS[status] ?? status;
  }

  public getStatusClass(status: OrderDocumentStatusEnum): string {
    return ORDER_DOCUMENT_STATUS_CLASSES[status] ?? 'bg-gray-400/15 text-gray-400';
  }

  public getNextStatus(status: OrderDocumentStatusEnum): OrderDocumentStatusEnum | null {
    return getNextOrderDocumentStatus(status);
  }

  /** Quick status change from the table — for now, every role may advance the status. */
  public onQuickStatusChange(doc: IOrderDocument, newStatus: OrderDocumentStatusEnum): void {
    if (!doc._id || doc.status === newStatus) return;

    this.orderDocumentService
      .updateStatus(doc._id, newStatus)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          doc.status = newStatus;
        },
        error: () => {
          console.error('Error updating order document status');
          this.toastService.showError('Fehler beim Ändern des Status');
        },
      });
  }

  public formatDate(date: Date | string | undefined): string {
    if (!date) return '—';
    return new Date(date).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit',
    });
  }

  /**
   * Lieferdatum column: the next upcoming Liefertermin (closest date >= now). If every
   * Liefertermin is already in the past, falls back to the most recent (closest) past one.
   */
  public getNextLieferdatum(doc: IOrderDocument): string {
    const termine = doc.deliveryDates;
    if (!termine?.length) return '—';

    const now = new Date();
    const upcoming = termine.filter(t => new Date(t.date) >= now);
    const closest = upcoming.length
      ? upcoming.reduce((a, b) => (new Date(a.date) <= new Date(b.date) ? a : b))
      : termine.reduce((a, b) => (new Date(a.date) >= new Date(b.date) ? a : b));

    return this.formatDate(closest.date);
  }

  /** Auftragsmenge column: a quantity of 0 (legacy numeric documents) means it's tracked on the Lieferschein instead. */
  public getOrderQuantityDisplay(doc: IOrderDocument): string {
    const value = doc.orderQuantity;
    if (String(value) === '0' || value == null || value === '') return 'Siehe LS';
    return String(value);
  }

  /** Only Wareneingang (and Admin, via canAccess) may create an order document. */
  public get canCreate(): boolean {
    return canAccess(this.currentUser?.role, [UserRole.WARENEINGANG]);
  }

  /** Only Wareneingang (and Admin, via canAccess) may delete an order document. */
  public get canDelete(): boolean {
    return canAccess(this.currentUser?.role, [UserRole.WARENEINGANG]);
  }

  /** Every role that may access the order-document pages may open a document to view/edit it. */
  public get canEdit(): boolean {
    return canAccess(this.currentUser?.role, ORDER_DOCUMENT_ROLES);
  }

  public openCreatePage(): void {
    this.router.navigate(['/order-documents/new']);
  }

  public openEditPage(doc: IOrderDocument): void {
    this.router.navigate(['/order-documents/edit', doc._id]);
  }

  public onDelete(doc: IOrderDocument): void {
    if (!doc._id) return;

    this.modalService
      .open(DeleteConfirmationComponent, {
        title: 'Auftragsbegleitschein löschen',
        message: 'Möchten Sie diesen Auftragsbegleitschein wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.',
      })
      .then((confirmed: boolean) => {
        if (!confirmed) return;
        this.orderDocumentService
          .delete(doc._id!)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe({
            next: () => {
              this.toastService.showSuccess('Auftragsbegleitschein erfolgreich gelöscht');
              this.loadOrderDocuments();
            },
            error: () => {
              console.error('Error deleting order document');
              this.toastService.showError('Fehler beim Löschen des Auftragsbegleitscheins');
            },
          });
      })
      .catch(() => { });
  }
}
